import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { PROTECTED_ROUTES } from "@/constants/routes";
import { useSellWizard } from "@/features/listings/context/SellWizardContext";
import { ImagePreviewGrid } from "@/features/listings/components/ImagePreviewGrid";
import { WizardActionBar } from "@/features/listings/components/WizardActionBar";
import { mediaStepSchema, type MediaStepValues } from "@/features/listings/schemas/listing.schemas";

/**
 * docs/ui/routes.md `/sell/media` — Create Listing Step 1. Only step whose
 * "Continue" is gated on file input rather than text fields, so the form
 * has exactly one field (`images`), driven through `Controller` since a
 * `File[]` isn't a native input value `register()` can bind to directly.
 */
export function SellMediaPage() {
  const navigate = useNavigate();
  const { draft, updateDraft } = useSellWizard();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<MediaStepValues>({
    resolver: zodResolver(mediaStepSchema),
    defaultValues: { images: draft.images },
  });

  function onSubmit(values: MediaStepValues) {
    updateDraft({ images: values.images });
    navigate(PROTECTED_ROUTES.sellDetails);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="space-y-1 pb-6">
        <h1 className="text-h2 text-foreground">Add photos</h1>
        <p className="text-body text-muted-foreground">
          Add up to 8 photos. The first one becomes your listing's cover photo.
        </p>
      </div>

      <Controller
        name="images"
        control={control}
        render={({ field }) => <ImagePreviewGrid images={field.value} onChange={field.onChange} />}
      />
      {errors.images && (
        <p className="mt-2 text-caption text-destructive" role="alert">
          {errors.images.message}
        </p>
      )}

      <WizardActionBar primaryLabel="Continue" onPrimary={handleSubmit(onSubmit)} />
    </form>
  );
}
